"use client";

import React, { useState, useMemo } from "react";
import type { FeedbackItem, FeedbackStatus } from "../types";
import { useMarkupStore } from "../store";
import { formatDate, cn, matchesStatusFilter, ticketMentions } from "../utils";
import { FilterIcon, CalendarIcon, ChevronRightIcon, InboxIcon, DesktopIcon, TabletIcon, MobileIcon, ArchiveIcon } from "./Icons";
import { archiveFeedbackBefore } from "../services/firestore";
import {
    buildMarkupMarkdown,
    downloadMarkdown,
    markupMarkdownFilename,
} from "../utils/markdownExport";

interface FeedbackListProps {
    feedbackItems: FeedbackItem[];
    onSelectFeedback: (feedback: FeedbackItem) => void;
}

const statusOptions: { value: FeedbackStatus | "all"; label: string }[] = [
    // "All" means all *active* — closed tickets are done with, so they only
    // appear when someone asks for them by picking Closed.
    { value: "all", label: "All active" },
    { value: "open", label: "Open" },
    { value: "in-progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];

const priorityColors: Record<string, string> = {
    low: "bg-matter-ink/5 text-gray-700",
    medium: "bg-matter-ink/10 text-gray-800",
    high: "bg-matter-accent/15 text-gray-800",
    critical: "bg-matter-accent text-white",
};

const statusColors: Record<string, string> = {
    open: "bg-matter-ink/10 text-gray-800",
    "in-progress": "bg-yellow-100 text-gray-800",
    resolved: "bg-matter-ink text-matter-cream",
    closed: "bg-matter-accent/15 text-gray-800",
};

/** Past its due date and not yet done. Compared as local calendar days. */
const isOverdue = (item: FeedbackItem): boolean => {
    if (!item.dueDate || item.status === "resolved" || item.status === "closed") {
        return false;
    }
    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return item.dueDate < todayKey;
};

export const FeedbackList: React.FC<FeedbackListProps> = ({
    feedbackItems,
    onSelectFeedback,
}) => {
    const {
        config,
        currentUser,
        setFeedbackItems,
        statusFilter,
        setStatusFilter,
        devExport,
    } = useMarkupStore();
    const [sortBy, setSortBy] = useState<"newest" | "oldest" | "priority" | "due">(
        "newest",
    );
    const [search, setSearch] = useState("");
    const [mineOnly, setMineOnly] = useState(false);
    const [taggedOnly, setTaggedOnly] = useState(false);
    const mentionsMe = (item: FeedbackItem) =>
        !!currentUser && ticketMentions(item, currentUser.id);

    // Bulk archive ("close out the last phase"). Restricted to admins and
    // developers — it can hide hundreds of tickets in one click.
    const canBulkArchive =
        currentUser?.role === "admin" || currentUser?.role === "developer";
    const [showArchiveTool, setShowArchiveTool] = useState(false);
    const [archiveDate, setArchiveDate] = useState(() =>
        new Date().toISOString().slice(0, 10),
    );
    const [isArchiving, setIsArchiving] = useState(false);
    const [archiveMessage, setArchiveMessage] = useState<string | null>(null);
    const [archiveError, setArchiveError] = useState<string | null>(null);

    // Tickets that the cutoff would archive, so the confirm button can say how
    // many. This counts what's loaded (already-archived tickets never reach the
    // store), which matches what the user can see.
    const cutoff = useMemo(() => {
        const [y, m, d] = archiveDate.split("-").map(Number);
        if (!y || !m || !d) return null;
        // Local midnight, so "before 4 Aug" means the user's 4 Aug, not UTC's.
        return new Date(y, m - 1, d);
    }, [archiveDate]);

    const archiveCandidates = useMemo(() => {
        if (!cutoff) return [];
        return feedbackItems.filter(
            (item) => new Date(item.createdAt).getTime() < cutoff.getTime(),
        );
    }, [feedbackItems, cutoff]);

    const handleBulkArchive = async () => {
        if (!cutoff || archiveCandidates.length === 0) return;
        setIsArchiving(true);
        setArchiveError(null);
        setArchiveMessage(null);
        try {
            const count = await archiveFeedbackBefore(config.projectId, cutoff);
            // The subscription re-fires and drops them anyway; this just avoids
            // a beat where archived tickets are still on screen.
            const archivedIds = new Set(archiveCandidates.map((i) => i.id));
            setFeedbackItems(
                feedbackItems.filter((item) => !archivedIds.has(item.id)),
            );
            setArchiveMessage(
                `Archived ${count} ticket${count === 1 ? "" : "s"}.`,
            );
            setShowArchiveTool(false);
        } catch (error) {
            console.error("Bulk archive failed:", error);
            setArchiveError(
                error instanceof Error ? error.message : "Archive failed",
            );
        } finally {
            setIsArchiving(false);
        }
    };

    // What the current filter puts in scope, before search narrows it further.
    // Closed tickets are out of scope unless they're what was asked for, so
    // finished work stops crowding the list and the page pins.
    const itemsInScope = useMemo(
        () =>
            feedbackItems.filter((item) =>
                matchesStatusFilter(item.status, statusFilter),
            ),
        [feedbackItems, statusFilter],
    );

    const hiddenClosedCount = useMemo(
        () =>
            statusFilter === "all"
                ? feedbackItems.filter((item) => item.status === "closed").length
                : 0,
        [feedbackItems, statusFilter],
    );

    const handleExportMarkdown = () => {
        const markdown = buildMarkupMarkdown(feedbackItems, {
            projectId: config.projectId,
        });
        downloadMarkdown(markdown, markupMarkdownFilename());
    };

    const filteredAndSortedItems = useMemo(() => {
        let items = [...itemsInScope];

        if (mineOnly && currentUser) {
            items = items.filter((item) => item.assignedTo?.id === currentUser.id);
        }

        if (taggedOnly && currentUser) {
            items = items.filter((item) => ticketMentions(item, currentUser.id));
        }

        // Filter by search
        if (search.trim()) {
            const q = search.toLowerCase();
            items = items.filter(
                (item) =>
                    item.title?.toLowerCase().includes(q) ||
                    item.description?.toLowerCase().includes(q) ||
                    item.currentCopy?.toLowerCase().includes(q) ||
                    item.newCopy?.toLowerCase().includes(q),
            );
        }

        // Sort
        items.sort((a, b) => {
            switch (sortBy) {
                case "newest":
                    return (
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                    );
                case "oldest":
                    return (
                        new Date(a.createdAt).getTime() -
                        new Date(b.createdAt).getTime()
                    );
                case "due": {
                    // Dated tickets first, soonest first; undated last.
                    const ad = a.dueDate ?? "9999-12-31";
                    const bd = b.dueDate ?? "9999-12-31";
                    return ad.localeCompare(bd);
                }
                case "priority": {
                    const priorityOrder = {
                        critical: 0,
                        high: 1,
                        medium: 2,
                        low: 3,
                    };
                    return (
                        priorityOrder[a.priority] - priorityOrder[b.priority]
                    );
                }
                default:
                    return 0;
            }
        });

        return items;
    }, [itemsInScope, sortBy, search, mineOnly, taggedOnly, currentUser]);

    // Group items by page URL
    const groupedByPage = useMemo(() => {
        const groups = new Map<string, FeedbackItem[]>();
        
        filteredAndSortedItems.forEach((item) => {
            const url = item.pageMetadata?.url || 'Unknown Page';
            if (!groups.has(url)) {
                groups.set(url, []);
            }
            groups.get(url)!.push(item);
        });

        // Convert to array and sort by count
        return Array.from(groups.entries())
            .map(([url, items]) => ({ url, items }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [filteredAndSortedItems]);

    // Extract pathname for cleaner display
    const getPathName = (url: string): string => {
        try {
            const urlObj = new URL(url);
            return urlObj.pathname === '/' ? 'Home' : urlObj.pathname;
        } catch {
            return url;
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Search */}
            <div className="relative mb-3">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                    type="text"
                    placeholder="Search feedback…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs! font-medium focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-0"
                        aria-label="Clear search"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Filter Bar */}
            <div className="flex gap-2 pb-4 mb-4">
                {/* Status Filter */}
                <div className="relative flex-1">
                    <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                        value={statusFilter}
                        onChange={(e) =>
                            setStatusFilter(
                                e.target.value as FeedbackStatus | "all",
                            )
                        }
                        className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs! font-medium appearance-none focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all"
                    >
                        {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Sort */}
                <div className="relative flex-1">
                    <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                        value={sortBy}
                        onChange={(e) =>
                            setSortBy(
                                e.target.value as
                                    | "newest"
                                    | "oldest"
                                    | "priority"
                                    | "due",
                            )
                        }
                        className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs! font-medium appearance-none focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all"
                    >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="priority">Priority</option>
                        <option value="due">Due date</option>
                    </select>
                </div>
            </div>

            {currentUser && feedbackItems.some((i) => i.assignedTo) && (
                <label className="flex items-center gap-2 -mt-2 mb-4 text-xs! text-gray-700 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={mineOnly}
                        onChange={(e) => setMineOnly(e.target.checked)}
                    />
                    Only tickets assigned to me
                </label>
            )}

            {currentUser && feedbackItems.some(mentionsMe) && (
                <label className="flex items-center gap-2 -mt-2 mb-4 text-xs! text-gray-700 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={taggedOnly}
                        onChange={(e) => setTaggedOnly(e.target.checked)}
                    />
                    Only tickets I’m tagged in
                </label>
            )}

            {/* Bulk archive — close out a phase by cutting off at a date */}
            {canBulkArchive && (
                <div className="-mt-2 mb-4">
                    {!showArchiveTool ? (
                        <div className="flex items-center gap-2">
                            <button type="button"
                                onClick={() => {
                                    setShowArchiveTool(true);
                                    setArchiveMessage(null);
                                    setArchiveError(null);
                                }}
                                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 cursor-pointer transition-colors"
                            >
                                <ArchiveIcon className="w-3.5 h-3.5" />
                                Archive older tickets
                            </button>
                            {archiveMessage && (
                                <span className="text-[11px] text-gray-500">
                                    {archiveMessage}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                            <div className="text-xs! font-semibold text-gray-800 mb-1">
                                Archive tickets created before
                            </div>
                            <div className="text-[11px] text-gray-500 mb-2">
                                They're hidden from the list and the page pins.
                                Nothing is deleted — the tickets stay in the
                                database.
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={archiveDate}
                                    onChange={(e) =>
                                        setArchiveDate(e.target.value)
                                    }
                                    className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs! font-medium focus:outline-none focus:border-matter-ink/40 transition-all"
                                />
                                <button type="button"
                                    onClick={() => {
                                        setShowArchiveTool(false);
                                        setArchiveError(null);
                                    }}
                                    className="px-2.5 py-2 text-xs! font-semibold text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button type="button"
                                    onClick={handleBulkArchive}
                                    className={cn(
                                        "px-2.5 py-2 text-xs! font-semibold text-white rounded-lg transition-colors",
                                        isArchiving ||
                                            archiveCandidates.length === 0
                                            ? "bg-gray-300 cursor-not-allowed"
                                            : "bg-gray-800 hover:bg-gray-900 cursor-pointer",
                                    )}
                                >
                                    {isArchiving
                                        ? "Archiving…"
                                        : `Archive ${archiveCandidates.length}`}
                                </button>
                            </div>
                            {archiveError && (
                                <div className="mt-2 text-[11px] text-red-600">
                                    {archiveError}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Hidden maintainer tool — revealed by the export shortcut
                (ctrl+shift+E by default), invisible to everyone else. */}
            {devExport && (
                <div className="-mt-2 mb-4">
                    <button type="button"
                        onClick={handleExportMarkdown}
                        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-800 cursor-pointer transition-colors"
                        title="Download every open and in-progress ticket as markdown, grouped by page"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                        </svg>
                        Save as .md
                    </button>
                </div>
            )}

            {/* Feedback Items */}
            <div className="flex-1 overflow-y-auto -mx-1 px-1">
                {filteredAndSortedItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                        <InboxIcon className="w-12 h-12 mb-3 opacity-50" />
                        <p className="text-sm! font-medium text-gray-500">
                            No feedback items
                        </p>
                        <p className="text-xs! text-gray-400 mt-1">
                            {search
                                ? `No results for "${search}"`
                                : statusFilter !== "all"
                                ? "No items match this filter — try a different status"
                                : hiddenClosedCount > 0
                                ? `Nothing open — ${hiddenClosedCount} closed ${hiddenClosedCount === 1 ? "ticket is" : "tickets are"} hidden. Filter by Closed to see ${hiddenClosedCount === 1 ? "it" : "them"}.`
                                : "Submit your first feedback using the New tab"}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {groupedByPage.map((group) => (
                            <div key={group.url} className="space-y-3">
                                <div className="pb-2 ml-2 border-b border-gray-200" title={group.url}>
                                    <div className="text-xs! font-medium text-gray-500 truncate">
                                        {getPathName(group.url)}
                                    </div>
                                </div>
                                
                                {/* Group Items */}
                                <div className="flex flex-col gap-3 pl-2">
                                    {group.items.map((item) => (
                                        <button type="button"
                                            key={item.id}
                                            data-feedback-id={item.id}
                                            onClick={() => onSelectFeedback(item)}
                                            className="w-full p-4 bg-white rounded-xl border border-gray-200 cursor-pointer text-left transition-all hover:shadow-lg hover:shadow-gray-200 hover:border-gray-300"
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2 flex-1">
                                                    <div className="text-sm! font-semibold text-gray-900 m-0 flex-1 line-clamp-2">
                                                        {item.title}
                                                    </div>
                                                    {item.pageMetadata?.deviceType && (
                                                        <div className="flex-shrink-0">
                                                            {item.pageMetadata.deviceType === 'desktop' && (
                                                                <DesktopIcon className="w-4 h-4 text-gray-400" />
                                                            )}
                                                            {item.pageMetadata.deviceType === 'tablet' && (
                                                                <TabletIcon className="w-4 h-4 text-gray-400" />
                                                            )}
                                                            {item.pageMetadata.deviceType === 'mobile' && (
                                                                <MobileIcon className="w-4 h-4 text-gray-400" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                            </div>

                                            {item.description && (
                                                <p className="text-xs! text-gray-600 m-0 mb-3 line-clamp-2">
                                                    {item.description}
                                                </p>
                                            )}

                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    className={cn(
                                                        "px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wide",
                                                        statusColors[item.status],
                                                    )}
                                                >
                                                    {item.status.replace("-", " ")}
                                                </span>
                                                <span
                                                    className={cn(
                                                        "px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wide",
                                                        priorityColors[item.priority],
                                                    )}
                                                >
                                                    {item.priority}
                                                </span>
                                                {item.type && (
                                                    <span className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-gray-100 text-gray-700">
                                                        {item.type}
                                                    </span>
                                                )}
                                                {mentionsMe(item) && (
                                                    <span className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-matter-accent/15 text-matter-ink">
                                                        @ You’re tagged
                                                    </span>
                                                )}
                                                {item.assignedTo && (
                                                    <span className="px-2.5 py-1 rounded-lg text-[10px] font-medium bg-white border border-gray-200 text-gray-700">
                                                        {item.assignedTo.name}
                                                    </span>
                                                )}
                                                {item.dueDate && (
                                                    <span
                                                        className={cn(
                                                            "px-2.5 py-1 rounded-lg text-[10px] font-medium",
                                                            isOverdue(item)
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-gray-100 text-gray-700",
                                                        )}
                                                    >
                                                        {isOverdue(item) ? "Overdue · " : "Due "}
                                                        {item.dueDate.split("-").reverse().join("/")}
                                                    </span>
                                                )}
                                                <span className="ml-auto text-[10px] text-gray-400">
                                                    {item.createdBy?.name && `${item.createdBy.name} · `}
                                                    {formatDate(item.createdAt)}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )
                }
            </div>

            {/* Item Count */}
            {feedbackItems.length > 0 && (
                <div className="pt-3 mt-3 border-t border-gray-200 text-xs! text-gray-400 text-center">
                    Showing {filteredAndSortedItems.length} of{" "}
                    {itemsInScope.length} items
                    {hiddenClosedCount > 0 &&
                        ` · ${hiddenClosedCount} closed hidden`}
                </div>
            )}
        </div>
    );
};

export default FeedbackList;
